import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import StatCard from '../components/StatCard';
import ChatBox from '../components/ChatBox';
import DetailedStageView from '../components/DetailedStageView';
import { GraduationCap, LogOut, Users, Clock, CheckCircle, FileText, Archive, UserX, Calendar, UserMinus, Eye, EyeOff, UserPlus, Building2, Link, Unlink, Trash2, Gift, Phone, Mail, UserCheck, Copy } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Record status display config
const RECORD_STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  closed_not_interested: { label: 'Not Interested', color: 'bg-red-100 text-red-800', icon: UserX },
  duplicate_record: { label: 'Duplicate', color: 'bg-gray-100 text-gray-800', icon: Archive },
  deferred_next_intake: { label: 'Deferred', color: 'bg-yellow-100 text-yellow-800', icon: Calendar },
  chosen_different_agent: { label: 'Different Agent', color: 'bg-orange-100 text-orange-800', icon: UserMinus }
};

// Referral status config
const REFERRAL_STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  contacted: { label: 'Contacted', color: 'bg-blue-100 text-blue-800' },
  enrolled: { label: 'Enrolled', color: 'bg-green-100 text-green-800' },
  not_interested: { label: 'Not Interested', color: 'bg-gray-100 text-gray-600' }
};

const REWARD_STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-600' },
  eligible: { label: 'Eligible', color: 'bg-purple-100 text-purple-800' },
  given: { label: 'Given', color: 'bg-green-100 text-green-800' }
};

const ConsultantDashboard = () => {
  const { user, logout } = useAuth();
  const [students, setStudents] = useState([]);
  const [agents, setAgents] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [showStageDialog, setShowStageDialog] = useState(false);
  const [showDocRequestDialog, setShowDocRequestDialog] = useState(false);
  const [showOfferLetterDialog, setShowOfferLetterDialog] = useState(false);
  const [showRecordStatusDialog, setShowRecordStatusDialog] = useState(false);
  const [showAgentDialog, setShowAgentDialog] = useState(false);
  const [showAssignAgentDialog, setShowAssignAgentDialog] = useState(false);
  const [showReferralsDialog, setShowReferralsDialog] = useState(false);
  const [showReferralUpdateDialog, setShowReferralUpdateDialog] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);;
  const [showManageAgentsDialog, setShowManageAgentsDialog] = useState(false);
  const [showClosedRecords, setShowClosedRecords] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stageUpdate, setStageUpdate] = useState({
    stage_index: 0,
    status: '',
    notes: ''
  });
  const [docRequest, setDocRequest] = useState({
    document_name: '',
    stage_index: 0,
    notes: ''
  });
  const [offerLetter, setOfferLetter] = useState({
    university_name: '',
    course_title: '',
    tuition_fee: '',
    scholarship_amount: '',
    country: '',
    offer_letter_url: ''
  });
  const [recordStatusUpdate, setRecordStatusUpdate] = useState({
    record_status: '',
    notes: ''
  });
  const [newAgent, setNewAgent] = useState({
    full_name: '',
    email: '',
    phone: '',
    company_name: '',
    commission_rate: '',
    password: ''
  });
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [uploadingOffer, setUploadingOffer] = useState(false);

  useEffect(() => {
    fetchStudents();
    fetchConsultations();
    fetchAgents();
    fetchReferrals();
  }, [showClosedRecords]);

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/consultant/students?include_closed=${showClosedRecords}`);
      setStudents(response.data);
    } catch (error) {
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/consultant/agents`);
      setAgents(response.data);
    } catch (error) {
      console.error('Failed to load agents:', error);
    }
  };

  const fetchReferrals = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/consultant/referrals`);
      setReferrals(response.data);
    } catch (error) {
      console.error('Failed to load referrals:', error);
    }
  };

  const fetchConsultations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/consultation/list`);
      setConsultations(response.data);
    } catch (error) {
      console.error('Failed to fetch consultations:', error);
    }
  };

  const handleUpdateReferral = async (status, rewardStatus, notes) => {
    if (!selectedReferral) return;

    try {
      await axios.put(`${API_URL}/api/consultant/referrals/${selectedReferral.id}`, {
        status,
        reward_status: rewardStatus,
        consultant_notes: notes
      });
      toast.success('Referral updated successfully');
      setShowReferralUpdateDialog(false);
      setSelectedReferral(null);
      fetchReferrals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update referral');
    }
  };

  const handleConvertReferral = async (referralId) => {
    try {
      const response = await axios.post(`${API_URL}/api/consultant/referrals/${referralId}/convert`);
      toast.success(
        <div>
          <p>Referral converted to student!</p>
          <p className="text-sm mt-1">Temp Password: <strong>{response.data.temporary_password}</strong></p>
        </div>,
        { duration: 10000 }
      );
      fetchReferrals();
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert referral');
    }
  };

  const handleStageUpdate = async () => {
    if (!selectedStudent) return;

    try {
      await axios.put(`${API_URL}/api/consultant/students/stage`, {
        student_id: selectedStudent.id,
        ...stageUpdate
      });
      toast.success('Stage updated successfully');
      setShowStageDialog(false);
      fetchStudents();
      setStageUpdate({ stage_index: 0, status: '', notes: '' });
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  const handleDocumentRequest = async () => {
    if (!selectedStudent) return;

    try {
      await axios.post(`${API_URL}/api/consultant/document-requests`, {
        student_id: selectedStudent.id,
        ...docRequest
      });
      toast.success('Document request sent');
      setShowDocRequestDialog(false);
      setDocRequest({ document_name: '', stage_index: 0, notes: '' });
    } catch (error) {
      toast.error('Failed to send document request');
    }
  };

  const handleOfferLetterUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingOffer(true);
    const reader = new FileReader();

    reader.onloadend = async () => {
      try {
        await axios.post(`${API_URL}/api/offer-letters/upload`, {
          student_id: selectedStudent.id,
          ...offerLetter,
          offer_letter_url: reader.result
        });
        toast.success('Offer letter uploaded successfully!');
        setShowOfferLetterDialog(false);
        setOfferLetter({
          university_name: '',
          course_title: '',
          tuition_fee: '',
          scholarship_amount: '',
          country: '',
          offer_letter_url: ''
        });
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to upload offer letter');
      } finally {
        setUploadingOffer(false);
      }
    };

    reader.readAsDataURL(file);
  };

  const handleRecordStatusUpdate = async () => {
    if (!selectedStudent || !recordStatusUpdate.record_status) {
      toast.error('Please select a status');
      return;
    }

    try {
      await axios.put(`${API_URL}/api/consultant/students/record-status`, {
        student_id: selectedStudent.id,
        record_status: recordStatusUpdate.record_status,
        notes: recordStatusUpdate.notes
      });
      toast.success('Record status updated successfully');
      setShowRecordStatusDialog(false);
      setRecordStatusUpdate({ record_status: '', notes: '' });
      
      // If we changed to a non-active status and not showing closed records, deselect
      if (recordStatusUpdate.record_status !== 'active' && !showClosedRecords) {
        setSelectedStudent(null);
      }
      
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update record status');
    }
  };

  const handleCreateAgent = async () => {
    if (!newAgent.full_name || !newAgent.email || !newAgent.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/consultant/agents`, newAgent);
      toast.success('Agent account created successfully!');
      setShowAgentDialog(false);
      setNewAgent({
        full_name: '',
        email: '',
        phone: '',
        company_name: '',
        commission_rate: '',
        password: ''
      });
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create agent');
    }
  };

  const handleAssignAgent = async () => {
    if (!selectedStudent) return;

    try {
      await axios.put(`${API_URL}/api/consultant/students/assign-agent`, {
        student_id: selectedStudent.id,
        agent_id: selectedAgentId || null
      });
      toast.success(selectedAgentId ? 'Agent assigned successfully!' : 'Agent assignment removed');
      setShowAssignAgentDialog(false);
      setSelectedAgentId('');
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign agent');
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm('Are you sure you want to delete this agent? This will remove them from all assigned students.')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/consultant/agents/${agentId}`);
      toast.success('Agent deleted successfully');
      fetchAgents();
      fetchStudents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete agent');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const activeStudents = students.filter(s => !s.record_status || s.record_status === 'active');
  const closedStudents = students.filter(s => s.record_status && s.record_status !== 'active');
  const totalStudents = activeStudents.length;
  const pendingApplications = activeStudents.filter(s => s.current_stage < 9).length;
  const completedApplications = activeStudents.filter(s => s.current_stage === 9 && s.stages[9]?.status === 'completed').length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-lime-50">
      <nav className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_edujourney-29/artifacts/h5igihub_britsfly.png" 
              alt="BritsFly Logo" 
              className="h-10 w-auto"
            />
            <span className="font-heading font-bold text-2xl text-foreground">BritsFly</span>
          </div>
          <div className="flex items-center gap-4">
            <Button
              data-testid="manage-referrals-btn"
              onClick={() => setShowReferralsDialog(true)}
              variant="outline"
              className="rounded-full"
            >
              <Gift className="h-4 w-4 mr-2" />
              Referrals ({referrals.filter(r => r.status === 'pending').length} pending)
            </Button>
            <Button
              data-testid="manage-agents-btn"
              onClick={() => setShowManageAgentsDialog(true)}
              variant="outline"
              className="rounded-full"
            >
              <Building2 className="h-4 w-4 mr-2" />
              Manage Agents ({agents.length})
            </Button>
            <div className="text-right">
              <p className="font-heading font-semibold text-foreground">{user?.full_name}</p>
              <p className="text-sm text-muted-foreground">Consultant</p>
            </div>
            <Button
              data-testid="logout-btn"
              onClick={logout}
              variant="outline"
              size="icon"
              className="rounded-full"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="font-heading font-bold text-4xl text-foreground mb-2">
            Consultant Dashboard
          </h1>
          <p className="text-muted-foreground text-lg">Manage student applications</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <StatCard
            title="Total Students"
            value={totalStudents}
            icon={Users}
          />
          <StatCard
            title="Pending Applications"
            value={pendingApplications}
            icon={Clock}
          />
          <StatCard
            title="Completed"
            value={completedApplications}
            icon={CheckCircle}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading font-semibold text-2xl">Student List</CardTitle>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-sm">
                      {showClosedRecords ? (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className="text-muted-foreground">Show Closed</span>
                      <Switch
                        data-testid="show-closed-toggle"
                        checked={showClosedRecords}
                        onCheckedChange={setShowClosedRecords}
                      />
                    </div>
                    {showClosedRecords && closedStudents.length > 0 && (
                      <span className="text-xs bg-muted px-2 py-1 rounded-full">
                        {closedStudents.length} closed
                      </span>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {students.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">
                      {showClosedRecords ? 'No students found' : 'No active students'}
                    </p>
                  ) : (
                    students.map((student) => {
                      const completedStages = student.stages.filter(s => s.status === 'completed').length;
                      const progressPercentage = (completedStages / student.stages.length) * 100;
                      const statusConfig = RECORD_STATUS_CONFIG[student.record_status || 'active'];
                      const isInactive = student.record_status && student.record_status !== 'active';

                      return (
                        <div
                          key={student.id}
                          data-testid={`student-card-${student.id}`}
                          onClick={() => setSelectedStudent(student)}
                          className={`p-5 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                            selectedStudent?.id === student.id
                              ? 'border-primary bg-primary/5'
                              : isInactive
                              ? 'border-muted bg-muted/30 opacity-70 hover:opacity-100'
                              : 'border-border hover:border-primary/50 hover:shadow-md'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="font-heading font-semibold text-lg text-foreground">
                                {student.full_name}
                              </h3>
                              <p className="text-sm text-muted-foreground">{student.email}</p>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              {isInactive ? (
                                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${statusConfig.color}`}>
                                  {statusConfig.label}
                                </span>
                              ) : (
                                <span className="text-xs font-bold uppercase px-3 py-1 rounded-full bg-primary/20 text-primary">
                                  Stage {student.current_stage + 1}/11
                                </span>
                              )}
                            </div>
                          </div>
                          {!isInactive && (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">Progress</span>
                                <span className="font-semibold text-foreground">{Math.round(progressPercentage)}%</span>
                              </div>
                              <div className="w-full bg-muted rounded-full h-2">
                                <div
                                  className="bg-primary h-2 rounded-full transition-all duration-300"
                                  style={{ width: `${progressPercentage}%` }}
                                />
                              </div>
                            </div>
                          )}
                          {student.record_status_notes && isInactive && (
                            <p className="text-xs text-muted-foreground mt-2 italic">
                              Note: {student.record_status_notes}
                            </p>
                          )}
                          {student.country && !isInactive && (
                            <p className="text-xs text-muted-foreground mt-2">From: {student.country}</p>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>

            {selectedStudent && (
              <>
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle className="font-heading font-semibold text-2xl">Consultation Requests</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {consultations.filter(c => c.student_id === selectedStudent.id).length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">No consultation requests</p>
                    ) : (
                      <div className="space-y-3">
                        {consultations.filter(c => c.student_id === selectedStudent.id).map((consultation) => (
                          <div key={consultation.id} className="p-4 rounded-xl border bg-card">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-foreground">
                                  {consultation.scheduled_date} at {consultation.scheduled_time}
                                </p>
                                <p className="text-sm text-muted-foreground mt-1">{consultation.description}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`text-xs px-3 py-1 rounded-full font-semibold ${
                                  consultation.status === 'confirmed' ? 'bg-success/20 text-success' :
                                  consultation.status === 'completed' ? 'bg-primary/20 text-primary' :
                                  'bg-warning/20 text-warning'
                                }`}>
                                  {consultation.status}
                                </span>
                                {consultation.status === 'pending' && (
                                  <Button
                                    size="sm"
                                    onClick={async () => {
                                      try {
                                        await axios.put(`${API_URL}/api/consultation/${consultation.id}/status?new_status=confirmed`);
                                        toast.success('Consultation confirmed');
                                        fetchConsultations();
                                      } catch (error) {
                                        toast.error('Failed to confirm');
                                      }
                                    }}
                                    className="rounded-full text-xs"
                                  >
                                    Confirm
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="font-heading font-semibold text-2xl">Student Journey</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="flex gap-3 flex-wrap">
                      <Button
                        data-testid="update-stage-btn"
                        onClick={() => setShowStageDialog(true)}
                        className="rounded-full bg-primary hover:bg-primary/90"
                      >
                        Update Stage
                      </Button>
                      <Button
                        data-testid="request-document-btn"
                        onClick={() => setShowDocRequestDialog(true)}
                        variant="outline"
                        className="rounded-full"
                      >
                        Request Document
                      </Button>
                      <Button
                        data-testid="upload-offer-letter-btn"
                        onClick={() => setShowOfferLetterDialog(true)}
                        variant="outline"
                        className="rounded-full border-success text-success hover:bg-success/10"
                      >
                        Upload Offer Letter
                      </Button>
                      <Button
                        data-testid="update-record-status-btn"
                        onClick={() => {
                          setRecordStatusUpdate({
                            record_status: selectedStudent.record_status || 'active',
                            notes: selectedStudent.record_status_notes || ''
                          });
                          setShowRecordStatusDialog(true);
                        }}
                        variant="outline"
                        className="rounded-full border-warning text-warning hover:bg-warning/10"
                      >
                        <Archive className="h-4 w-4 mr-2" />
                        Record Status
                      </Button>
                      <Button
                        data-testid="assign-agent-btn"
                        onClick={() => {
                          setSelectedAgentId(selectedStudent.assigned_agent_id || '');
                          setShowAssignAgentDialog(true);
                        }}
                        variant="outline"
                        className="rounded-full border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                      >
                        {selectedStudent.assigned_agent_id ? (
                          <>
                            <Link className="h-4 w-4 mr-2" />
                            Agent: {selectedStudent.assigned_agent_name}
                          </>
                        ) : (
                          <>
                            <UserPlus className="h-4 w-4 mr-2" />
                            Assign Agent
                          </>
                        )}
                      </Button>
                    </div>

                    <div className="space-y-3">
                      {selectedStudent.stages.map((stage, index) => (
                        <DetailedStageView
                          key={index}
                          stage={stage}
                          stageIndex={index}
                          studentId={selectedStudent.id}
                          onRefresh={fetchStudents}
                        />
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
              </>
            )}
          </div>

          <div className="lg:col-span-5">
            {selectedStudent ? (
              <div className="sticky top-6">
                <ChatBox studentId={selectedStudent.id} currentUserRole="consultant" />
              </div>
            ) : (
              <Card>
                <CardContent className="py-20 text-center">
                  <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Select a student to view details and chat</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      <Dialog open={showStageDialog} onOpenChange={setShowStageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold text-2xl">Update Stage</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="stage">Stage</Label>
              <Select
                value={stageUpdate.stage_index.toString()}
                onValueChange={(value) => setStageUpdate({ ...stageUpdate, stage_index: parseInt(value) })}
              >
                <SelectTrigger data-testid="stage-select">
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent>
                  {selectedStudent?.stages.map((stage, index) => (
                    <SelectItem key={index} value={index.toString()}>
                      {index + 1}. {stage.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={stageUpdate.status}
                onValueChange={(value) => setStageUpdate({ ...stageUpdate, status: value })}
              >
                <SelectTrigger data-testid="status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                data-testid="stage-notes-input"
                value={stageUpdate.notes}
                onChange={(e) => setStageUpdate({ ...stageUpdate, notes: e.target.value })}
                placeholder="Add any notes..."
                className="rounded-xl"
              />
            </div>

            <Button
              data-testid="save-stage-btn"
              onClick={handleStageUpdate}
              className="w-full rounded-full bg-primary hover:bg-primary/90"
            >
              Update Stage
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showDocRequestDialog} onOpenChange={setShowDocRequestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold text-2xl">Request Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="doc-name">Document Name</Label>
              <Input
                id="doc-name"
                data-testid="doc-request-name-input"
                value={docRequest.document_name}
                onChange={(e) => setDocRequest({ ...docRequest, document_name: e.target.value })}
                placeholder="e.g., Passport Copy"
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="doc-stage">Related Stage</Label>
              <Select
                value={docRequest.stage_index.toString()}
                onValueChange={(value) => setDocRequest({ ...docRequest, stage_index: parseInt(value) })}
              >
                <SelectTrigger data-testid="doc-request-stage-select">
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent>
                  {selectedStudent?.stages.map((stage, index) => (
                    <SelectItem key={index} value={index.toString()}>
                      {index + 1}. {stage.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="doc-notes">Instructions</Label>
              <Textarea
                id="doc-notes"
                data-testid="doc-request-notes-input"
                value={docRequest.notes}
                onChange={(e) => setDocRequest({ ...docRequest, notes: e.target.value })}
                placeholder="Provide instructions for the student..."
                className="rounded-xl"
              />
            </div>

            <Button
              data-testid="send-doc-request-btn"
              onClick={handleDocumentRequest}
              className="w-full rounded-full bg-primary hover:bg-primary/90"
            >
              Send Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showOfferLetterDialog} onOpenChange={setShowOfferLetterDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold text-2xl">Upload Offer Letter</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="uni-name">University Name</Label>
              <Input
                id="uni-name"
                data-testid="offer-uni-name-input"
                value={offerLetter.university_name}
                onChange={(e) => setOfferLetter({ ...offerLetter, university_name: e.target.value })}
                placeholder="e.g., University of Oxford"
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="course-name">Course Title</Label>
              <Input
                id="course-name"
                data-testid="offer-course-name-input"
                value={offerLetter.course_title}
                onChange={(e) => setOfferLetter({ ...offerLetter, course_title: e.target.value })}
                placeholder="e.g., MSc Computer Science"
                className="rounded-xl"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tuition">Tuition Fee</Label>
                <Input
                  id="tuition"
                  data-testid="offer-tuition-input"
                  value={offerLetter.tuition_fee}
                  onChange={(e) => setOfferLetter({ ...offerLetter, tuition_fee: e.target.value })}
                  placeholder="e.g., £25,000"
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="scholarship">Scholarship Amount</Label>
                <Input
                  id="scholarship"
                  data-testid="offer-scholarship-input"
                  value={offerLetter.scholarship_amount}
                  onChange={(e) => setOfferLetter({ ...offerLetter, scholarship_amount: e.target.value })}
                  placeholder="e.g., £5,000 or None"
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                data-testid="offer-country-input"
                value={offerLetter.country}
                onChange={(e) => setOfferLetter({ ...offerLetter, country: e.target.value })}
                placeholder="e.g., United Kingdom"
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="offer-file">Upload Offer Letter (PDF)</Label>
              <Input
                id="offer-file"
                type="file"
                accept=".pdf,image/*"
                onChange={handleOfferLetterUpload}
                disabled={uploadingOffer || !offerLetter.university_name || !offerLetter.course_title}
                className="rounded-xl"
              />
              <p className="text-xs text-muted-foreground">Please fill all fields before uploading</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showRecordStatusDialog} onOpenChange={setShowRecordStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold text-2xl">Update Record Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-muted/50 rounded-xl p-4">
              <p className="text-sm text-muted-foreground">
                Changing the status of <span className="font-semibold text-foreground">{selectedStudent?.full_name}</span>
              </p>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={recordStatusUpdate.record_status}
                onValueChange={(value) => setRecordStatusUpdate({ ...recordStatusUpdate, record_status: value })}
              >
                <SelectTrigger data-testid="record-status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>Active - Ongoing application</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="closed_not_interested">
                    <div className="flex items-center gap-2">
                      <UserX className="h-4 w-4 text-red-600" />
                      <span>Closed - Not Interested</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="duplicate_record">
                    <div className="flex items-center gap-2">
                      <Archive className="h-4 w-4 text-gray-600" />
                      <span>Duplicate Record</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="deferred_next_intake">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-yellow-600" />
                      <span>Deferred for Next Intake</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="chosen_different_agent">
                    <div className="flex items-center gap-2">
                      <UserMinus className="h-4 w-4 text-orange-600" />
                      <span>Chosen Different Agent</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status-notes">Notes (Reason for status change)</Label>
              <Textarea
                id="status-notes"
                data-testid="record-status-notes-input"
                value={recordStatusUpdate.notes}
                onChange={(e) => setRecordStatusUpdate({ ...recordStatusUpdate, notes: e.target.value })}
                placeholder="Add notes about why the status is being changed..."
                className="rounded-xl"
                rows={3}
              />
            </div>

            {recordStatusUpdate.record_status && recordStatusUpdate.record_status !== 'active' && (
              <div className="bg-warning/10 border border-warning/30 rounded-xl p-4">
                <p className="text-sm text-warning-foreground">
                  <strong>Note:</strong> This student will be hidden from the main list. 
                  Enable "Show Closed" to view archived records.
                </p>
              </div>
            )}

            <Button
              data-testid="save-record-status-btn"
              onClick={handleRecordStatusUpdate}
              className="w-full rounded-full bg-primary hover:bg-primary/90"
            >
              Update Status
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Agents Dialog */}
      <Dialog open={showManageAgentsDialog} onOpenChange={setShowManageAgentsDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold text-2xl flex items-center justify-between">
              <span>Manage Agents</span>
              <Button
                data-testid="add-agent-btn"
                onClick={() => {
                  setShowManageAgentsDialog(false);
                  setShowAgentDialog(true);
                }}
                className="rounded-full bg-emerald-600 hover:bg-emerald-700"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add New Agent
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {agents.length === 0 ? (
              <div className="text-center py-8">
                <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No agents yet</p>
                <p className="text-sm text-muted-foreground">Create your first agent to start tracking referrals</p>
              </div>
            ) : (
              agents.map((agent) => (
                <div key={agent.id} className="p-4 border rounded-xl hover:bg-muted/50">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-foreground">{agent.full_name}</h3>
                      <p className="text-sm text-muted-foreground">{agent.email}</p>
                      {agent.company_name && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                          <Building2 className="h-3 w-3" />
                          {agent.company_name}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right mr-4">
                        <p className="text-lg font-bold text-emerald-600">{agent.total_referrals}</p>
                        <p className="text-xs text-muted-foreground">Referrals</p>
                      </div>
                      <Button
                        size="icon"
                        variant="outline"
                        className="rounded-full text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteAgent(agent.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="mt-3 flex gap-4 text-sm">
                    <span className="text-muted-foreground">
                      Active: <strong className="text-foreground">{agent.active_referrals}</strong>
                    </span>
                    <span className="text-muted-foreground">
                      Completed: <strong className="text-foreground">{agent.successful_applications}</strong>
                    </span>
                    {agent.commission_rate && (
                      <span className="text-muted-foreground">
                        Commission: <strong className="text-foreground">{agent.commission_rate}%</strong>
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Agent Dialog */}
      <Dialog open={showAgentDialog} onOpenChange={setShowAgentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold text-2xl">Create New Agent</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="agent-name">Full Name *</Label>
              <Input
                id="agent-name"
                data-testid="agent-name-input"
                value={newAgent.full_name}
                onChange={(e) => setNewAgent({ ...newAgent, full_name: e.target.value })}
                placeholder="John Smith"
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="agent-email">Email *</Label>
              <Input
                id="agent-email"
                type="email"
                data-testid="agent-email-input"
                value={newAgent.email}
                onChange={(e) => setNewAgent({ ...newAgent, email: e.target.value })}
                placeholder="agent@company.com"
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="agent-password">Password *</Label>
              <Input
                id="agent-password"
                type="password"
                data-testid="agent-password-input"
                value={newAgent.password}
                onChange={(e) => setNewAgent({ ...newAgent, password: e.target.value })}
                placeholder="Create a password"
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="agent-phone">Phone</Label>
              <Input
                id="agent-phone"
                data-testid="agent-phone-input"
                value={newAgent.phone}
                onChange={(e) => setNewAgent({ ...newAgent, phone: e.target.value })}
                placeholder="+44 123 456 7890"
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="agent-company">Company/Agency Name</Label>
              <Input
                id="agent-company"
                data-testid="agent-company-input"
                value={newAgent.company_name}
                onChange={(e) => setNewAgent({ ...newAgent, company_name: e.target.value })}
                placeholder="Global Education Partners"
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="agent-commission">Commission Rate (%)</Label>
              <Input
                id="agent-commission"
                type="number"
                data-testid="agent-commission-input"
                value={newAgent.commission_rate}
                onChange={(e) => setNewAgent({ ...newAgent, commission_rate: e.target.value })}
                placeholder="10"
                className="rounded-xl"
              />
            </div>

            <Button
              data-testid="create-agent-btn"
              onClick={handleCreateAgent}
              className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700"
            >
              Create Agent Account
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Agent to Student Dialog */}
      <Dialog open={showAssignAgentDialog} onOpenChange={setShowAssignAgentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold text-2xl">Assign Agent to Student</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-muted/50 rounded-xl p-4">
              <p className="text-sm text-muted-foreground">
                Assigning agent to <span className="font-semibold text-foreground">{selectedStudent?.full_name}</span>
              </p>
            </div>

            <div className="space-y-2">
              <Label>Select Agent</Label>
              <Select
                value={selectedAgentId || "none"}
                onValueChange={(value) => setSelectedAgentId(value === "none" ? "" : value)}
              >
                <SelectTrigger data-testid="assign-agent-select">
                  <SelectValue placeholder="Select an agent" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Unlink className="h-4 w-4" />
                      <span>No Agent (Remove assignment)</span>
                    </div>
                  </SelectItem>
                  {agents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-emerald-600" />
                        <span>{agent.full_name}</span>
                        {agent.company_name && (
                          <span className="text-muted-foreground text-xs">({agent.company_name})</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {agents.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-sm text-amber-800">
                  No agents available. <button className="underline font-medium" onClick={() => {
                    setShowAssignAgentDialog(false);
                    setShowAgentDialog(true);
                  }}>Create an agent first</button>.
                </p>
              </div>
            )}

            <Button
              data-testid="save-agent-assignment-btn"
              onClick={handleAssignAgent}
              className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700"
              disabled={agents.length === 0}
            >
              {selectedAgentId ? 'Assign Agent' : 'Remove Assignment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Referrals Management Dialog */}
      <Dialog open={showReferralsDialog} onOpenChange={setShowReferralsDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold text-2xl flex items-center gap-2">
              <Gift className="h-6 w-6 text-primary" />
              Student Referrals
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {referrals.length === 0 ? (
              <div className="text-center py-8">
                <Gift className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No referrals yet</p>
                <p className="text-sm text-muted-foreground">Referrals from students will appear here</p>
              </div>
            ) : (
              referrals.map((referral) => {
                const statusConfig = REFERRAL_STATUS_CONFIG[referral.status];
                const rewardConfig = REWARD_STATUS_CONFIG[referral.reward_status];
                
                return (
                  <div key={referral.id} className="p-4 border rounded-xl hover:bg-muted/50">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-foreground">
                          {referral.friend_first_name} {referral.friend_last_name}
                        </h3>
                        <p className="text-sm text-muted-foreground flex items-center gap-2">
                          <Phone className="h-3 w-3" />
                          {referral.friend_phone}
                        </p>
                        {referral.friend_email && (
                          <p className="text-sm text-muted-foreground flex items-center gap-2">
                            <Mail className="h-3 w-3" />
                            {referral.friend_email}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                          Referred by: <strong>{referral.referrer_name}</strong>
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Date: {new Date(referral.created_at).toLocaleDateString()}
                        </p>
                        {referral.consultant_notes && (
                          <p className="text-sm text-muted-foreground mt-2 italic">
                            Notes: {referral.consultant_notes}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className={`text-xs font-semibold px-3 py-1 rounded-full ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                        <span className={`text-xs px-3 py-1 rounded-full ${rewardConfig.color}`}>
                          Reward: {rewardConfig.label}
                        </span>
                        <div className="flex gap-2 mt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full"
                            onClick={() => {
                              setSelectedReferral(referral);
                              setShowReferralUpdateDialog(true);
                            }}
                          >
                            Update
                          </Button>
                          {!referral.converted_student_id && referral.status !== 'not_interested' && (
                            <Button
                              size="sm"
                              className="rounded-full bg-success hover:bg-success/90"
                              onClick={() => handleConvertReferral(referral.id)}
                            >
                              <UserCheck className="h-4 w-4 mr-1" />
                              Convert
                            </Button>
                          )}
                          {referral.converted_student_id && (
                            <span className="text-xs text-success flex items-center gap-1">
                              <CheckCircle className="h-4 w-4" />
                              Converted
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Update Referral Dialog */}
      <Dialog open={showReferralUpdateDialog} onOpenChange={setShowReferralUpdateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold text-2xl">Update Referral</DialogTitle>
          </DialogHeader>
          {selectedReferral && (
            <ReferralUpdateForm
              referral={selectedReferral}
              onUpdate={handleUpdateReferral}
              onClose={() => setShowReferralUpdateDialog(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Separate component for referral update form
const ReferralUpdateForm = ({ referral, onUpdate, onClose }) => {
  const [status, setStatus] = useState(referral.status);
  const [rewardStatus, setRewardStatus] = useState(referral.reward_status);
  const [notes, setNotes] = useState(referral.consultant_notes || '');

  return (
    <div className="space-y-4">
      <div className="bg-muted/50 rounded-xl p-4">
        <p className="font-medium">{referral.friend_first_name} {referral.friend_last_name}</p>
        <p className="text-sm text-muted-foreground">
          Referred by: {referral.referrer_name}
        </p>
      </div>

      <div className="space-y-2">
        <Label>Status</Label>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="enrolled">Enrolled</SelectItem>
            <SelectItem value="not_interested">Not Interested</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Reward Status</Label>
        <Select value={rewardStatus} onValueChange={setRewardStatus}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="eligible">Eligible for Reward</SelectItem>
            <SelectItem value="given">Reward Given</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes about this referral..."
          className="rounded-xl"
          rows={3}
        />
      </div>

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={onClose}
          className="flex-1 rounded-full"
        >
          Cancel
        </Button>
        <Button
          onClick={() => onUpdate(status, rewardStatus, notes)}
          className="flex-1 rounded-full bg-primary hover:bg-primary/90"
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default ConsultantDashboard;